import PropTypes from "prop-types";
import { Box, CircularProgress, Drawer, IconButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ArrowRight } from "lucide-react";

import { Button } from "../../../components/Button";
import { useTheme as useCustomTheme } from "../../../theme/useTheme";

/**
 * Right-hand side panel with Clientify's layout: grey header with the title
 * and a → to close, "Información básica" with labels on the left, and a fixed
 * footer with Cancelar / Guardar.
 */
export const ClientifySidePanel = ({
  open,
  title,
  onClose,
  onSubmit,
  canSubmit,
  saving = false,
  loading = false,
  footerStart = null,
  header = null,
  headerActions = null,
  sectionTitle = "Información básica",
  showFooter = true,
  width = 640,
  children,
}) => {
  const theme = useTheme();
  const { neutrals } = useCustomTheme();

  const handleClose = () => {
    if (!saving) onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{ zIndex: 1300 }}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: width },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2.25,
          backgroundColor: neutrals.gray[100],
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: "1.125rem", fontWeight: 600 }}>{title}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {headerActions}
          <IconButton aria-label="Cerrar" onClick={handleClose} disabled={saving}>
            <ArrowRight size={20} />
          </IconButton>
        </Box>
      </Box>

      {header && <Box sx={{ flexShrink: 0 }}>{header}</Box>}

      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {sectionTitle && (
              <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", mb: 2 }}>
                {sectionTitle}
              </Typography>
            )}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>{children}</Box>
          </>
        )}
      </Box>

      {showFooter && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 3,
          py: 1.75,
          borderTop: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>{footerStart}</Box>
        <Box sx={{ ml: "auto", display: "flex", gap: 1.5 }}>
          <Button variant="ghost" onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!canSubmit || saving || loading}
            sx={{ minWidth: 120 }}
          >
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </Box>
      </Box>
      )}
    </Drawer>
  );
};

ClientifySidePanel.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  canSubmit: PropTypes.bool,
  saving: PropTypes.bool,
  loading: PropTypes.bool,
  footerStart: PropTypes.node,
  header: PropTypes.node,
  headerActions: PropTypes.node,
  sectionTitle: PropTypes.string,
  showFooter: PropTypes.bool,
  width: PropTypes.number,
  children: PropTypes.node,
};

/** One "label | field" row, label right-aligned like Clientify's form. */
export const PanelField = ({ label, required = false, error, children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "170px 1fr" },
        columnGap: 2.5,
        rowGap: 0.5,
        alignItems: "start",
      }}
    >
      <Typography
        component="label"
        sx={{
          fontSize: "0.875rem",
          fontWeight: 500,
          textAlign: { xs: "left", sm: "right" },
          pt: { xs: 0, sm: "9px" },
          color: theme.palette.text.primary,
        }}
      >
        {label}
        {required && (
          <Box component="span" sx={{ color: theme.palette.error.main, ml: 0.25 }}>
            *
          </Box>
        )}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        {children}
        {error && (
          <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

PanelField.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  error: PropTypes.string,
  children: PropTypes.node,
};
